/**
 * threadArchiveNotice — announce a thread being archived FOR EVERYONE
 * (owner directive 2026-08-26).
 *
 * "If a user is currently within a thread that another user is actively
 * archiving, there should be a 5 second delay and a message clear as day that
 * appears (much like in the style of how workspaces are changed) … then the
 * other users are safely taken to the next best thread."
 *
 * Module-level store + subscribe, the workspaceTransition.ts pattern: the
 * thread store (useChatThreads) detects the remote archive and owns the
 * timing + navigation; the chrome (ThreadArchiveNoticeOverlay, mounted by
 * GlobalChatSystem) subscribes here and only ever displays. Neither imports
 * the other.
 *
 * Two kinds:
 *   • 'blocking' — the viewer is INSIDE the thread. Full overlay, the 5s
 *     hold, then the store navigates them to the next best thread and clears.
 *   • 'passing' — the thread was archived while the viewer was elsewhere.
 *     A transient toast-style line; no navigation, no hold.
 */

/** The owner-directed hold before the viewer is moved off the thread. */
export const THREAD_ARCHIVE_NOTICE_HOLD_MS = 5_000;
/** How long a passing notice stays up (matches the archive toast rhythm). */
export const THREAD_ARCHIVE_PASSING_NOTICE_MS = 5_000;

export interface ThreadArchiveNotice {
  kind: 'blocking' | 'passing';
  threadId: string;
  threadTitle: string;
  /** Who archived it, when the fanout carried a name. Empty ⇒ generic copy. */
  archivedByName?: string;
}

type Listener = (notice: ThreadArchiveNotice | null) => void;

let current: ThreadArchiveNotice | null = null;
const listeners = new Set<Listener>();

export function getThreadArchiveNotice(): ThreadArchiveNotice | null {
  return current;
}

export function subscribeThreadArchiveNotice(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(): void {
  listeners.forEach((listener) => {
    try {
      listener(current);
    } catch { /* one bad listener must not mute the rest */ }
  });
}

export function beginThreadArchiveNotice(notice: ThreadArchiveNotice): void {
  // A blocking notice is never displaced by a passing one — being moved off
  // the OPEN thread is the announcement that must not be missed.
  if (current?.kind === 'blocking' && notice.kind === 'passing') return;
  current = notice;
  emit();
}

export function clearThreadArchiveNotice(threadId?: string): void {
  if (threadId && current && current.threadId !== threadId) return;
  current = null;
  emit();
}
